// Helper di formattazione e colore-per-stampo del modulo M2 (separati dai
// componenti in shared.tsx per non rompere il fast-refresh di Vite).
import type { CapacitaUM } from "@/types/m2";

// ── Colore per STAMPO (doc #2: i blocchi del Gantt sono colorati per stampo) ──
export interface ColoreStampo {
  bg: string;
  border: string;
  text: string;
}

const STAMPO_PALETTE: ColoreStampo[] = [
  { bg: "#e3f0e8", border: "#2f6d4f", text: "#14331f" }, // verde
  { bg: "#e4eef7", border: "#3b6ea5", text: "#1b3a5c" }, // blu
  { bg: "#f8edd6", border: "#c08a2e", text: "#6b4d12" }, // oro
  { bg: "#efe8f6", border: "#7c5aa8", text: "#3f2c5a" }, // viola
  { bg: "#f7e6ea", border: "#b15c6e", text: "#5e2531" }, // rosa
  { bg: "#e0f0f0", border: "#4a8f8f", text: "#194747" }, // teal
  { bg: "#fdeede", border: "#c2772e", text: "#5e3812" }, // arancio
  { bg: "#e8ede0", border: "#6f8f3f", text: "#33401a" }, // oliva
];

const STAMPO_FISSI: Record<string, number> = {
  T006: 0,
  T003: 1,
  T208: 2,
  T212: 3,
  T007: 4,
  T100: 5,
};

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function coloreStampo(stampo: string | null | undefined): ColoreStampo {
  if (!stampo) return { bg: "#f1f5f9", border: "#94a3b8", text: "#475569" };
  const i = STAMPO_FISSI[stampo] ?? hashStr(stampo) % STAMPO_PALETTE.length;
  return STAMPO_PALETTE[i];
}

export function saturazioneColor(pct: number): string {
  if (pct > 100) return "bg-red-500";
  if (pct >= 85) return "bg-amber-500";
  return "bg-primary";
}

export const CAP_UM_LABEL: Record<CapacitaUM, string> = {
  t_giorno: "t/g",
  pz_giorno: "pz/g",
  kg_giorno: "kg/g",
};

/**
 * Numeri in italiano, **col raggruppamento forzato**.
 *
 * ⚠️ In `it-IT` `useGrouping` di default è «auto» e salta i numeri a quattro
 * cifre: «9700 kg/h» finiva accanto a «10.600 kg/h» nella stessa colonna. Stesso
 * difetto già trovato in Commesse il 16-08 — va forzato, non dedotto.
 */
export function num(n: number, decimali = 0): string {
  return n.toLocaleString("it-IT", {
    useGrouping: true,
    minimumFractionDigits: decimali,
    maximumFractionDigits: decimali,
  });
}

export function fmtMinuti(m: number | null | undefined): string {
  if (!m || m <= 0) return "—";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h ? `${h}h ${String(mm).padStart(2, "0")}` : `${mm} min`;
}

export function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function fmtFinestra(da: string | null, a: string | null): string {
  if (!da && !a) return "—";
  const opt: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  const fmt = (s: string) => new Date(s).toLocaleString("it-IT", opt).replace(",", "");
  return `${da ? fmt(da) : "—"} → ${a ? fmt(a) : "—"}`;
}
